import type { AxiosError } from 'axios'
import type { ExceptionResult } from '@/api/auth'

export interface ParsedError {
  message: string
  fieldErrors?: Record<string, string[]>
}

export function getError(error: unknown): ParsedError {
  if (!error) {
    return { message: 'An unknown error occurred' }
  }

  // Handle Axios errors
  if (isAxiosError(error)) {
    const data = error.response?.data as ExceptionResult | undefined

    if (data?.errors) {
      // Has field-specific errors
      return {
        message: data.message || 'Validation failed',
        fieldErrors: data.errors,
      }
    }

    // Has message only
    if (data?.message) {
      return { message: data.message }
    }

    // Generic axios error
    if (error.response?.status) {
      const statusMessages: Record<number, string> = {
        400: 'Bad request',
        401: 'Unauthorized',
        403: 'Forbidden',
        404: 'Resource not found',
        422: 'Validation failed',
        429: 'Too many requests',
        500: 'Server error',
        502: 'Bad gateway',
        503: 'Service unavailable',
      }

      return {
        message: statusMessages[error.response.status] || `Error ${error.response.status}`,
      }
    }
  }

  // Handle Error objects
  if (error instanceof Error) {
    return { message: error.message }
  }

  // Handle strings
  if (typeof error === 'string') {
    return { message: error }
  }

  // Fallback
  return { message: 'An unknown error occurred' }
}

export function isAxiosError(error: unknown): error is AxiosError {
  return (
    !!error &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  )
}

export function getFieldError(
  fieldErrors: Record<string, string[]> | undefined,
  fieldName: string,
): string | undefined {
  if (!fieldErrors) {
    return undefined
  }
  return fieldErrors[fieldName]?.[0]
}
